<template>
  <section>
    <FormulateForm
      v-if="product"
      ref="formProduct"
      name="formProduct"
      @submit="localSaveProduct"
    >
      <b-card-actions
        action-collapse
        :show-loading="fetching || saving"
        :title="
          productDescription
            ? `${product.id ? `#${product.id} - ` : ''}${productDescription}`
            : $t('Produto')
        "
      >
        <b-row>
          <!-- image side -->
          <b-col md="3">
            <b-row>
              <b-col md="12">
                <b-form-group class="w-100 border-0">
                  <b-form-radio-group
                    id="active"
                    v-model="product.active"
                    :options="[
                      { value: true, text: 'Ativo' },
                      { value: false, text: 'Inativo' },
                    ]"
                    button-variant="primary"
                    size="md"
                    class="w-100 border-0"
                    buttons
                    :disabled="isReadOnly"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="12">
                <b-tabs fill>
                  <b-tab
                    active
                    :title="$t('Quadrada')"
                  >
                    <FormulateInput
                      id="image-square-local"
                      v-model="imageSquareLocal"
                      :type="isReadOnly ? 'image' : 'uploader'"
                      deletable="true"
                      max-files="1"
                      :meta="false"
                    />
                  </b-tab>
                  <b-tab :title="$t('Wide')">
                    <FormulateInput
                      id="image-wide-local"
                      v-model="imageWideLocal"
                      :type="isReadOnly ? 'image' : 'uploader'"
                      deletable="true"
                      max-files="1"
                      :meta="false"
                    />
                  </b-tab>
                </b-tabs>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="12">
                <div style="overflow-x: auto">
                  <barcode
                    class="text-center"
                    :value="product.ean || 1234567890"
                  >
                    EAN
                  </barcode>
                </div>
                <FormulateInput
                  v-if="!isReadOnly"
                  id="ean"
                  v-model.trim="product.ean"
                  class="mt-1"
                  :label="$t('Código de Barras (EAN/GTIN)')"
                  type="text"
                  inputmode="numeric"
                  :pattern="/[0-9]*/"
                  maxlength="15"
                />
              </b-col>
            </b-row>
          </b-col>
          <b-col md="9">
            <product-form
              ref="productForm"
              :purchase-order-product="purchaseOrderProduct"
            />
          </b-col>
        </b-row>
      </b-card-actions>

      <b-card-actions
        :show-loading="fetching || saving"
        action-collapse
        :title="'Características'"
      >
        <!-- each tab has it your own FomulateForm -->
        <b-tabs fill>
          <b-tab :title="$t('Preços')">
            <sku-form
              ref="skuForm"
              :fetching="fetching || fetchingPriceTables || saving"
            />
          </b-tab>

          <b-tab
            v-if="product.tax"
            :title="$t('Fiscal')"
          >
            <div>
              <b-row align-v="center">
                <b-col md="12">
                  <b-row>
                    <b-col md="5">
                      <FormulateInput
                        id="tax-type"
                        v-model="product.tax.productTaxType"
                        :type="isReadOnly ? 'label' : 'vue-select'"
                        :label="$t('Tipo')"
                        :placeholder="$t('Selecione')"
                        :options="productTaxTypes()"
                      />
                    </b-col>
                    <b-col md="7">
                      <FormulateInput
                        id="ncm"
                        v-model="product.tax.ncmCode"
                        :type="isReadOnly ? 'label' : 'select-searchable'"
                        :label="$t('NCM')"
                        :placeholder="$t('Digite para pesquisar')"
                        :options="getComboNcms"
                        :start-search="product.tax.ncmCode"
                        autofocus
                        :class="isSaleClassification ? 'required' : ''"
                        :validation="isSaleClassification ? 'required' : ''"
                        :instruction="
                          $t('Digite pelo menos 3 numeros ou letras para iniciar a pesquisa')
                        "
                        @fetchSearch="ncmSearch"
                      />
                    </b-col>
                    <b-col md="5">
                      <FormulateInput
                        id="origin"
                        v-model="product.tax.productOrigin"
                        :type="isReadOnly ? 'label' : 'vue-select'"
                        :label="$t('Origem')"
                        :placeholder="$t('Selecione')"
                        :options="productOrigins()"
                      />
                    </b-col>
                    <b-col md="7">
                      <FormulateInput
                        id="cest-id"
                        v-model="product.tax.cestId"
                        :type="isReadOnly ? 'label' : 'select-searchable'"
                        :instruction="
                          $t('Digite pelo menos 3 numeros ou letras para iniciar a pesquisa')
                        "
                        :placeholder="$t('Digite para pesquisar')"
                        :label="$t('CEST')"
                        :options="getComboCests"
                        @fetchSearch="cestSearch"
                      />
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>

              <b-row class="mt-2">
                <b-col md="6">
                  <FormulateInput
                    id="product-tax-configuration-group"
                    v-model="product.skus[0].taxConfigurationGroupId"
                    :type="isReadOnly ? 'label' : 'vue-select'"
                    :label="$t('Exceção Fiscal')"
                    :placeholder="$t('Usa a classificação fiscal do NCM')"
                    :instruction="
                      $t(
                        'Cadastro reutilizável que substitui a classificação fiscal do NCM nas UFs configuradas nele. Para associar vários produtos de uma vez, use a tela de Exceções Fiscais.'
                      )
                    "
                    :options="getComboTaxConfigurationGroups"
                  />
                </b-col>
                <b-col
                  md="6"
                  class="d-flex align-items-center"
                >
                  <e-button
                    v-if="$can('Read', 'TaxConfigurationGroup')"
                    id="manage-tax-configuration-groups"
                    variant="outline-primary"
                    icon="external-link"
                    :text="$t('Gerenciar exceções fiscais')"
                    @click="goToTaxConfigurationGroups"
                  />
                </b-col>
              </b-row>
            </div>
          </b-tab>

          <b-tab :title="$t('Configurações')">
            <sku-configuration
              ref="skuConfigurationForm"
              :fetching="fetching || fetchingPriceTables || saving"
              @add-configuration="addSkuConfiguration"
              @update-configuration="updateSkuConfiguration"
              @delete-configuration="deleteSkuConfiguration"
            />
          </b-tab>

          <b-tab
            v-if="product.integrateWithBeerCooler"
            :title="$t('Mídia da Chopeira')"
          >
            <tap-station-media-form
              :files="localTapStationMedia"
              :is-read-only="isReadOnly"
              @set-files="setTapStationMedia"
              @remove-file="removeTapStationMedia"
            />
          </b-tab>
        </b-tabs>
      </b-card-actions>

      <e-crud-actions
        :show-save="!isReadOnly"
        :show-edit="isReadOnly && $can('Update', 'Product')"
        :busy="saving || !ready"
        @save="localSaveProduct"
        @cancel="$router.back()"
        @edit="edit"
      />
    </FormulateForm>
  </section>
</template>

<script>
import { BRow, BCol, BFormGroup, BFormRadioGroup, BTabs, BTab } from 'bootstrap-vue'
import VueBarcode from 'vue-barcode'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { ECrudActions, EButton } from '@/views/components'
import { productDomains, uploader } from '@/mixins'
import i18n from '@/libs/i18n'
import { isJson } from '@/utils/object-utils'
import ProductForm from './components/ProductForm.vue'
import SkuForm from './components/SkuForm.vue'
import TapStationMediaForm from './components/TapStationMediaForm.vue'

import SkuConfiguration from './components/SkuConfiguration.vue'

export default {
  name: 'ProductMaintain',
  components: {
    BCardActions,
    BRow,
    BCol,
    BTabs,
    BTab,
    BFormGroup,
    BFormRadioGroup,
    ProductForm,
    SkuForm,
    barcode: VueBarcode,
    ECrudActions,
    EButton,
    TapStationMediaForm,
    SkuConfiguration,
  },

  mixins: [productDomains, uploader],

  data() {
    return {
      ready: false,
      fetching: false,
      fetchingPriceTables: false,
      saving: false,
      busy: false,
      imageWideLocal: [],
      imageSquareLocal: [],
      purchaseOrderData: {
        purchaseOrder: null,
        itemIndex: null,
      },
      callbackRouteName: 'product-list',
    }
  },

  computed: {
    ...mapState('pages/catalog/products/productMaintain', [
      'isReadOnly',
      'product',
      'tax',
      'taxCombos',
      'sidebarState',
      'selectedVariants',
      'tieredPrices',
      'tapStationMedia',
      'tapStationMediaDeleted',
      'configurations',
    ]),
    ...mapGetters('pages/catalog/products/productMaintain', [
      'productDescription',
      'isSaleClassification',
      'isAssetProductType',
      'configurationsForSave',
    ]),
    ...mapGetters('pages/invoice/taxConfigurationGroups', ['getComboTaxConfigurationGroups']),
    ...mapGetters('common/ncms', ['getComboNcms']),
    ...mapGetters('common/cests', ['getComboCests']),
    ncm() {
      return this.product?.tax?.ncmCode
    },
    purchaseOrderProduct() {
      return this.purchaseOrderData?.purchaseOrder?.items[this.purchaseOrderData?.itemIndex]
    },
    localTapStationMedia: {
      get() {
        return this.tapStationMedia
      },
      set(val) {
        this.setTapStationMedia(val)
      },
    },
  },

  watch: {
    product(val) {
      this.imageSquareLocal = val?.imageSquare ? [this.getFileInstance(val.imageSquare)] : []
      this.imageWideLocal = val?.imageWide ? [this.getFileInstance(val.imageWide)] : []
    },
    ncm(val) {
      if (val) {
        this.cestSearch(() => {})
      }
      // O seletor de exceção fiscal só pode oferecer os cadastros compatíveis com o NCM atual.
      this.loadTaxConfigurationGroups()
    },
  },

  async mounted() {
    await this.cleanState()

    this.setReadOnly(this.$route.name === 'product-view')

    if (this.$route.params.id) {
      await this.getProduct(this.$route.params.id)
      this.$refs.productForm.fillDefaultOptions()
      this.cestSearch(() => {})
    } else {
      this.purchaseOrderData = this.$route.params.purchaseOrderData || {}
      if (this.purchaseOrderData.purchaseOrder) {
        this.product.ean =
          this.purchaseOrderData.purchaseOrder.items[
            this.purchaseOrderData.itemIndex
          ]?.xmlProduct?.eanTaxed
      }
    }
    this.getPriceTables()
    this.loadTaxConfigurationGroups()
    this.callbackRouteName = this.$route.params.callbackRouteName || 'product-list'
    this.$refs.productForm.bindGroupListener()
    // Espera o watcher do produto preencher as imagens locais antes de liberar o salvar.
    await this.$nextTick()
    this.ready = true
  },

  methods: {
    ...mapActions('pages/catalog/products', ['cleanCombo', 'fetchProductCombos']),
    ...mapActions('pages/catalog/products/productMaintain', [
      'fetchProduct',
      'saveProduct',
      'fetchPriceTables',
      'cleanState',
      'updateAssociatedSku',
      'setReadOnly',
      'setTapStationMedia',
      'uploadTapStationMedia',
      'deleteTapStationMedia',
      'deleteTapStationMediaFromServer',
      'addSkuConfiguration',
      'updateSkuConfiguration',
      'deleteSkuConfiguration',
    ]),
    ...mapActions('pages/invoice/taxConfigurationGroups', [
      'fetchTaxConfigurationGroupsAvailableForNcm',
    ]),
    ...mapActions('common/ncms', ['fetchNcms']),
    ...mapActions('common/cests', ['fetchCestsByNcm']),
    ...mapActions('pages/purchase/purchaseOrder/purchaseOrderMaintain', ['updateAssociatedSku']),

    edit() {
      this.$nextTick(() => {
        this.$router.push({ name: 'product-maintain', params: { id: this.$route.params.id } })
      })
    },

    selectGroup(idValue) {
      this.product.subgroup = null
      this.setComboSubgroups(idValue)
    },

    async getProduct(id) {
      try {
        this.fetching = true
        await this.fetchProduct(id)
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },

    async getPriceTables() {
      try {
        this.fetchingPriceTables = true
        await this.fetchPriceTables()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetchingPriceTables = false
      }
    },

    async ncmSearch(loading, search) {
      try {
        loading(true)
        await this.fetchNcms({ itemSearch: search })
      } catch (e) {
        this.showError({
          message: this.$t('Não foi possível fazer a pesquisa'),
        })
      } finally {
        loading(false)
      }
    },

    async cestSearch(loading, search) {
      if (this.product?.tax?.ncmCode) {
        try {
          loading(true)
          await this.fetchCestsByNcm({ ncm: this.product?.tax?.ncmCode, cestSearch: search })
        } catch (e) {
          this.showError({
            message: this.$t('Não foi possível fazer a pesquisa'),
          })
        } finally {
          loading(false)
        }
      }
    },

    isItemsPerDeliveryPackageValid() {
      const value = this.product.itemsPerDeliveryPackage
      if (value === null || value === undefined || value === '') {
        this.product.itemsPerDeliveryPackage = null
        return true
      }

      const normalizedValue = Number(value)
      const isValid =
        Number.isInteger(normalizedValue) && normalizedValue >= 1 && normalizedValue <= 9999
      if (isValid) this.product.itemsPerDeliveryPackage = normalizedValue
      return isValid
    },

    async localSaveProduct() {
      if (!this.ready || this.fetching || this.saving) return
      try {
        this.$refs.formProduct.showErrors()
        if (!this.isItemsPerDeliveryPackageValid()) {
          this.showInvalidDataMessage({
            message: this.$t(
              'PRODUCT.VALIDATION_ERROR.ITEMS_PER_DELIVERY_PACKAGE_OUT_OF_RANGE'
            ),
          })
          return
        }

        if (this.$refs.formProduct.isValid) {
          let success = true
          this.saving = true
          const uploadResult = await this.uploadImages()
          const [imageWide, imageSquare] = uploadResult

          const productPayload = {
            ...this.product,
            variations: this.product.variations?.map(variationId => ({ variationId })),
            name: this.productDescription,
            tags: this.product.tags.join(','),
            imageWide: imageWide?.url,
            imageSquare: imageSquare?.url,
          }
          try {
            await this.saveProduct(productPayload)
          } catch (error) {
            success = false

            const keys = Object.keys(error.response.data)
            const firstMessage = error.response.data[keys[0]] ? error.response.data[keys[0]][0] : ''
            if (isJson(firstMessage)) {
              const { message, args } = JSON.parse(firstMessage)
              this.showError({ message: i18n.t(message, args) })
            } else this.showError({ error })
          }
          if (success) {
            const newPrices = this.$refs.skuForm.getNewPrices()
            if (newPrices.length) {
              try {
                await this.$http({
                  url: '/api/products/skus/update-price',
                  method: 'PUT',
                  data: newPrices,
                })
              } catch (error) {
                success = false
                this.showError({ error })
              }
            }

            // Salvar configurações do SKU
            const { new: newConfigurations, updated: updatedConfigurations, deleted: deletedConfigurations } = this.configurationsForSave
            if (newConfigurations.length || updatedConfigurations.length || deletedConfigurations.length) {
              try {
                const [sku] = this.product.skus
                const configurationsPayload = {
                  skuId: sku.id,
                  new: newConfigurations,
                  updated: updatedConfigurations,
                  deleted: deletedConfigurations.map(c => c.id),
                }
                await this.$http({
                  url: '/api/products/skus/update-configuration',
                  method: 'PUT',
                  data: configurationsPayload,
                })
              } catch (error) {
                success = false
                this.showError({ error })
              }
            }

            await this.processTapStationMedia(this.product)

            if (this.tieredPrices?.length) {
              try {
                const [sku] = this.product.skus
                await this.$http({
                  url: '/api/products/skus/update-tiered-price',
                  method: 'PUT',
                  data: this.tieredPrices.map(tieredPrice => ({
                    ...tieredPrice,
                    priceTableId: +tieredPrice.priceTableId,
                    storeId: tieredPrice.storeId || null,
                    skuId: sku.id,
                  })),
                })
              } catch (error) {
                success = false
                this.showError({ error })
              }
            }
          }
          if (success) {
            this.showSuccess({ message: this.$t('Produto salvo com sucesso') })
            await this.executePosSaveActions()
            this.navigateBack()
          }
        } else {
          this.showInvalidDataMessage()
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },

    navigateBack() {
      this.$router.push({ name: this.callbackRouteName })
    },

    async updatePurchaseOrder() {
      const [sku] = (await this.$http.get(`/api/skus/search?search=${this.product.ean}`)).data
        .results
      this.updateAssociatedSku({ sku, itemIndex: this.purchaseOrderData.itemIndex })
    },

    async executePosSaveActions() {
      switch (this.callbackRouteName) {
        case 'purchase-order-xml-import':
          this.updatePurchaseOrder()
          break
        default:
          break
      }
    },

    updateSkuConfiguration(config) {
      this.$store.dispatch('pages/catalog/products/productMaintain/updateSkuConfiguration', {
        configuration: config,
      })
    },

    deleteSkuConfiguration(config) {
      this.$store.dispatch('pages/catalog/products/productMaintain/deleteSkuConfiguration', {
        configurationId: config.id,
      })
    },

    async loadTaxConfigurationGroups() {
      try {
        await this.fetchTaxConfigurationGroupsAvailableForNcm(this.ncm)
      } catch (error) {
        // O seletor é opcional: falhar aqui não pode impedir o cadastro do produto.
        this.showError({ error })
      }
    },

    goToTaxConfigurationGroups() {
      const { href } = this.$router.resolve({ name: 'tax-configuration-group-list' })
      window.open(href, '_blank')
    },

    isValidProductData() {
      try {
        const { variations, hasVariations } = this.product

        if (hasVariations) {
          const hasEmptyFieldsVariations = variations.some(vari =>
            this.selectedVariants.some(sv => vari[sv] === undefined || vari[sv] === '')
          )

          if (hasEmptyFieldsVariations) {
            throw new Error(this.$t('Há campos faltando nas variações de produto'))
          }
        }

        return true
      } catch (err) {
        this.showInvalidDataMessage({ message: `<b>${err.message}</b>` })
        return false
      }
    },

    async uploadImages() {
      let resultWide = null
      let resultSquare = null
      const result = await this.upload({
        files: [...this.imageWideLocal, ...this.imageSquareLocal],
        destinationPath: 'images/products',
      })
      if (this.imageWideLocal.length) {
        ;[resultWide] = result
      }
      if (this.imageSquareLocal.length) {
        ;[resultSquare] = result.slice(result.length > 1 ? 1 : 0)
      }
      const nullResult = { url: null }
      return [resultWide || nullResult, resultSquare || nullResult]
    },

    async processTapStationMedia(product) {
      let hasFileError = false
      try {
        await this.uploadTapStationMedia(product?.id)
      } catch (uploadError) {
        this.showError({
          title: this.$t('Ocorreu um erro ao fazer upload das mídias'),
          error: uploadError,
        })
        hasFileError = true
      }

      try {
        await this.deleteTapStationMediaFromServer(product?.id)
      } catch (deleteError) {
        this.showError({
          title: this.$t('Ocorreu um erro ao deletar as mídias'),
          error: deleteError,
        })
        hasFileError = true
      }

      if (
        !hasFileError &&
        (this.tapStationMedia.some(file => !file.id) || this.tapStationMediaDeleted.length > 0)
      ) {
        this.showSuccess({ message: this.$t('Mídias salvas com sucesso.') })
      }
    },

    async removeTapStationMedia(file) {
      this.deleteTapStationMedia(file)
    },
  },
}
</script>

<style lanb="scss" scoped>
.btn-group-toggle label.btn:not(.active) {
  background-color: gray !important;
}
</style>
